import React, { useMemo, useRef } from "react";
import JsPDF from "jspdf";
import html2canvas from "html2canvas";
import styled from "styled-components";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
} from "chart.js";
import Chevron from "../../../../../assets/images/icons/Web_Sprite_Icon_Chevron_Grey_analytics.png";
import CrownIcon from "../../../../../assets/images/icons/Web_Sprite_Icon_Analytics_Crown.png";
import TZLogo from "../../../../../assets/images/logos/colorLogo.png";
import HeroLogoIntraverse from "../../../../../assets/images/logos/HeroLogoIntraverse.png";
import { capitalizeFirstLetter, createCrownPlugin } from "../../../../../utils/generalUtils";
import { ReactComponent as ExportIcon } from "../../../../../assets/images/icons/Mediamodifier-Design.svg";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BarColorPalette = [
    "#BA4BED",
    "#B056F7",
    "#A460FF",
    "#9869FF",
    "#8A72FF",
    "#7B7CFF",
    "#6B85FF",
    "#5D8EFF",
    "#4E96FF",
    "#3E9FFF",
    "#2EA7FF",
    "#1EAFFF",
];

const crownPlugin = {
    id: "crownPlugin",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const firstBar = chart.getDatasetMeta(0).data[0];
        if (firstBar) {
            const img = new Image();
            img.src = CrownIcon;
            const iconSize = 24;
            const x = firstBar.x - iconSize / 2;
            const y = firstBar.y - iconSize - 5;
            ctx.drawImage(img, x, y, iconSize, iconSize);
        }
    },
};
const rightLabelPlugin = {
    id: "rightLabelPlugin",
    afterDatasetsDraw(chart) {
        if (chart.options.indexAxis !== "y") return; // only horizontal
        const { ctx } = chart;
        chart.getDatasetMeta(0).data.forEach((bar, index) => {
            const label = chart.data.labels[index];
            const value = chart.data.datasets[0].data[index];
            const x = bar.x + 8; // small padding to the right
            const y = bar.y;
            ctx.fillStyle = "#fbfcff";
            ctx.font = "14px Barlow";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(label, x, y);
        });
    },
};
const rightLabelPluginPDF = {
    id: "rightLabelPluginPDF",
    afterDatasetsDraw(chart) {
        if (chart.options.indexAxis !== "y") return; // only horizontal

        const { ctx, chartArea } = chart;
        const dataset = chart.data.datasets[0];
        const meta = chart.getDatasetMeta(0);

        meta.data.forEach((bar, index) => {
            let label = chart.data.labels[index] || "";
            const value = dataset.data[index];

            const barWidth = bar.x - chartArea.left;
            const y = bar.y;

            ctx.font = "9px Barlow";
            ctx.textBaseline = "middle";
            ctx.fontStyle = "normal";

            // measure text width
            let textWidth = ctx.measureText(label).width;
            const padding = 10;

            // if bar is wide enough → put inside
            if (barWidth > textWidth + padding * 2) {
                // shorten if too long for bar
                while (textWidth > barWidth - padding * 2) {
                    label = `${label.slice(0, -4)}...`;
                    textWidth = ctx.measureText(label).width;
                }
                ctx.fillStyle = "#000000";
                ctx.textAlign = "left";
                ctx.fillText(label, chartArea.left + padding, y);
            } else {
                // otherwise, draw outside
                const maxOutsideWidth = chartArea.right - bar.x - 20;
                if (textWidth > maxOutsideWidth) {
                    // shorten for outside too
                    while (textWidth > maxOutsideWidth && label.length > 4) {
                        label = `${label.slice(0, -4)}...`;
                        textWidth = ctx.measureText(label).width;
                    }
                }
                ctx.fillStyle = "#fbfcff"; // light text outside
                ctx.textAlign = "left";
                ctx.fillText(label, bar.x + 8, y);
            }
        });
    },
};

const HorizontalBarChart = ({
    chartTitle,
    data = [],
    totalVisit = 0,
    Filter,
    ImageContainer,
    displayDate,
    isHorizontalBarChartModalOpen,
    setIsHorizontalBarChartModalOpen,
    type = "",
    pdfStartDate,
    pdfEndDate,
    client,
}) => {
    const chartRef = useRef(null);
    const maxLabelLength = 5;
    const getLabel = (item) => {
        if (type === "devices") {
            return `${item.vendor || "Unknown"} ${item.model || ""}`.trim();
        }
        return item.name || "";
    };

    const maxItems = isHorizontalBarChartModalOpen ? data?.length : maxLabelLength;
    const visibleData = data.slice(0, maxItems);
    const shouldRotate =
        visibleData.length > maxLabelLength && visibleData.some((item) => getLabel(item).length > maxLabelLength);

    let chartData = {
        labels: visibleData.map(getLabel),
        datasets: [
            {
                label: chartTitle,
                data: visibleData.map((item) => item.visits),
                backgroundColor: BarColorPalette.slice(0, maxItems),
                barThickness: isHorizontalBarChartModalOpen ? 10 : 15,
                categoryPercentage: isHorizontalBarChartModalOpen ? 1 : 0.3,
                barPercentage: isHorizontalBarChartModalOpen ? 0.8 : 0.3,
            },
        ],
    };

    if (type === "devices") {
        chartData = {
            labels: visibleData.map((item) => `${item.vendor || "Unknown"} ${item.model || ""}`),
            datasets: [
                {
                    label: chartTitle,
                    data: visibleData.map((item) => item.visits),
                    backgroundColor: BarColorPalette.slice(0, maxItems),
                    barThickness: isHorizontalBarChartModalOpen ? 10 : 15,
                    categoryPercentage: isHorizontalBarChartModalOpen ? 1 : 0.3,
                    barPercentage: isHorizontalBarChartModalOpen ? 0.8 : 0.3,
                },
            ],
        };
    }

    const options = useMemo(
        () => ({
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    right: 80,
                    bottom: 30,
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: "rgba(0,0,0,0.8)",
                    titleFont: { family: "Barlow", size: 12, weight: 700 },
                    bodyFont: { family: "Barlow", size: 12, weight: 400 },
                    displayColors: false,
                    callbacks: {
                        label: (ctx) => `${ctx.raw.toLocaleString()} ${chartTitle.toLowerCase()}`,
                    },
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: { color: "#fbfcff", font: { family: "Barlow", size: 12 } },
                    grid: { color: "rgba(255, 255, 255, 0.1)" },
                },
                y: {
                    ticks: {
                        color: "#fbfcff",
                        font: { family: "Barlow", size: 12 },
                        display: isHorizontalBarChartModalOpen,
                    },
                    grid: { display: false },
                },
            },
        }),
        [displayDate, shouldRotate, isHorizontalBarChartModalOpen]
    );

    const handleExportPDF = async () => {
        if (!data || data.length === 0) return;

        const pdf = new JsPDF("landscape", "pt", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 40;
        const chartMaxHeight = 300;
        const paddingBetweenChartAndLegends = 20;
        const itemsPerPage = 10;
        const totalPages = Math.ceil(data.length / itemsPerPage);

        for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
            // get first page first bar's index only
            const firstIndex = pageIndex * itemsPerPage;
            const start = pageIndex * itemsPerPage;
            const end = start + itemsPerPage;
            const pageData = data.slice(start, end);

            const barCount = pageData.length;

            // ✅ Force canvas size to match PDF drawing area
            const canvasWidth = pageWidth - 2 * margin;
            const offCanvas = document.createElement("canvas");
            offCanvas.width = canvasWidth;
            offCanvas.height = chartMaxHeight;

            // Chart.js uses style size for layout
            offCanvas.style.width = `${canvasWidth}px`;
            offCanvas.style.height = `${chartMaxHeight}px`;
            offCanvas.setAttribute("style", "all: unset; background: #000;");

            const ctx = offCanvas.getContext("2d");

            // Max visits
            const maxVisits = pageData.reduce((max, item) => Math.max(max, Number(item.visits)), 0);

            // Decide number of y steps
            let steps;
            if (pageData.length <= 5) {
                steps = 4;
            } else if (pageData.length <= 10) {
                steps = 5;
            } else {
                steps = 6;
            }

            // Compute nice max with headroom
            const niceMax = Math.ceil((maxVisits * 1.15) / 10) * 10;
            const stepSize = Math.ceil(niceMax / steps / 10) * 10;

            const chartData1 = {
                labels: pageData.map(getLabel),
                datasets: [
                    {
                        label: chartTitle,
                        data: pageData.map((item) => item.visits),
                        backgroundColor: BarColorPalette.slice(0, pageData.length),
                        barThickness: isHorizontalBarChartModalOpen ? 10 : 15,
                        categoryPercentage: isHorizontalBarChartModalOpen ? 1 : 0.3,
                        barPercentage: isHorizontalBarChartModalOpen ? 0.8 : 0.3,
                    },
                ],
            };

            const tempChart = new ChartJS(ctx, {
                type: "bar",
                data: chartData1,
                options: {
                    indexAxis: "y",
                    responsive: false,
                    maintainAspectRatio: false,
                    animation: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: { enabled: false },
                        datalabels: {
                            anchor: "end",
                            align: "end",
                            color: "#fbfcff",
                            font: { family: "Barlow", size: 10 },
                        },
                    },
                    layout: {
                        padding: {
                            right: 80,
                            bottom: 30,
                        },
                    },
                    scales: {
                        x: {
                            ticks: {
                                color: "#fbfcff",
                                font: { family: "Barlow", size: 10 },
                            },
                            grid: { display: false },
                            border: { display: false },
                        },
                        y: {
                            beginAtZero: true,
                            ticks: {
                                color: "#fbfcff",
                                font: { family: "Barlow", size: 10 },
                                display: isHorizontalBarChartModalOpen,
                            },
                            grid: { display: false },
                        },
                    },
                    backgroundColor: "#000000",
                },
                plugins: [
                    createCrownPlugin({
                        pageIndex,
                        allData: data,
                        onlyFirstPage: true,
                    }),
                    rightLabelPluginPDF,
                ],
            });

            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 100));
            const chartImage = offCanvas.toDataURL("image/png", 1.0);

            // Black background
            pdf.setFillColor(0, 0, 0);
            pdf.rect(0, 0, pageWidth, pageHeight, "F");

            // ✅ Add watermark
            pdf.saveGraphicsState();
            pdf.setGState(new pdf.GState({ opacity: 0.2 })); // 10% opacity
            const wmWidth = 450;
            const wmHeight = 400;
            const wmX = (pageWidth - wmWidth) / 2; // center horizontally
            const wmY = pageHeight * 0.15;
            pdf.addImage(HeroLogoIntraverse, "PNG", wmX, wmY, wmWidth, wmHeight, "", "NONE", 0);

            pdf.restoreGraphicsState();
            // Header
            const headerY = 30;
            const logoWidth = 70;
            const logoHeight = 70;
            pdf.addImage(TZLogo, "PNG", margin, headerY, logoWidth, logoHeight);
            const spacingBetweenLogos = 15;
            const textStartX = margin + logoWidth + spacingBetweenLogos + 10;

            pdf.setFontSize(14);
            pdf.setTextColor(255, 255, 255);
            pdf.text(`${capitalizeFirstLetter(client)}: ${chartTitle}`, textStartX, headerY + 20, { align: "left" });

            pdf.setFontSize(10);
            pdf.text(`Duration: ${displayDate}`, textStartX, headerY + 40, { align: "left" });
            pdf.text(`(${pdfStartDate} - ${pdfEndDate})`, textStartX, headerY + 55, { align: "left" });

            // Chart below header
            const chartTop = headerY + logoHeight + 30;
            pdf.addImage(chartImage, "PNG", margin, chartTop, canvasWidth, chartMaxHeight);

            // Legends layout
            const footerHeight = 40;
            const legendStartY = chartTop + chartMaxHeight + paddingBetweenChartAndLegends;
            const legendEndY = pageHeight - footerHeight - margin;
            const availableHeightForLegends = legendEndY - legendStartY;

            pdf.setFontSize(9);
            pdf.setTextColor(255, 255, 255);

            const rows = 5; // ✅ fixed number of rows
            const cols = 4; // ✅ fixed number of columns
            const minLineHeight = 18;
            const actualCols = Math.ceil(pageData.length / rows);
            const colWidth = (pageWidth - 2 * margin) / actualCols;

            pageData.forEach((item, index) => {
                const colIndex = Math.floor(index / rows); // fill by row, then move to next column
                const rowIndex = index % rows;

                const xBase = margin + colIndex * colWidth;
                const y = legendStartY + rowIndex * minLineHeight + minLineHeight;

                // ✅ Color swatch
                const swatchSize = 10;
                const swatchX = xBase;
                const swatchY = y - swatchSize + 2;
                pdf.setFillColor(BarColorPalette[index % BarColorPalette.length]);
                pdf.rect(swatchX, swatchY, swatchSize, swatchSize, "F");

                // ✅ Name (normal)
                const nameX = swatchX + swatchSize + 3;
                let label = getLabel(item);
                if (label.length > 80) {
                    label = `${label.slice(0, 77)}...`;
                }
                pdf.setFont("helvetica", "normal");
                pdf.setTextColor(255, 255, 255);
                pdf.text(label, nameX, y);

                // ✅ Visits (with dotted leader line, normal)
                const value = item.visits.toLocaleString();
                const nameWidth = pdf.getTextWidth(label);
                const valueWidth = pdf.getTextWidth(value);
                const dotsStartX = nameX + nameWidth + 2;
                const valueX = xBase + colWidth - 10;
                const dotsEndX = valueX - valueWidth - 4;

                pdf.setFont("helvetica", "normal");
                pdf.setTextColor(150, 150, 150);
                let dotX = dotsStartX;
                while (dotX < dotsEndX) {
                    pdf.text(".", dotX, y);
                    dotX += 2.5;
                }

                pdf.setTextColor(255, 255, 255);
                pdf.text(value, valueX, y, { align: "right" });
            });

            // Footer page numbers
            pdf.setFontSize(9);
            pdf.setTextColor(180, 180, 180);
            pdf.text(`Page ${pageIndex + 1} of ${totalPages}`, pageWidth / 2, pageHeight - 20, { align: "center" });

            tempChart.destroy();
            if (pageIndex < totalPages - 1) pdf.addPage();
        }
        const filename = `${client.toLowerCase()}_${chartTitle.replace(/\s+/g, "_").toLowerCase()}_${Date.now()}.pdf`;
        pdf.save(filename);
    };

    return (
        <>
            <HorizontalBarChartContainer>
                <HorizontalBarChartHeader>
                    {!isHorizontalBarChartModalOpen && (
                        <>
                            <HorizontalBarChartTitle>{chartTitle}</HorizontalBarChartTitle>
                            {totalVisit > 0 && (
                                <IconWrapper onClick={handleExportPDF}>
                                    <StyledExportIcon />
                                    <TooltipIcon>Export PDF</TooltipIcon>
                                </IconWrapper>
                            )}
                        </>
                    )}

                    {isHorizontalBarChartModalOpen && (
                        <>
                            <PopupDateTitle>{displayDate}</PopupDateTitle>
                            <TotalVisits>
                                Total Visits: <span>{totalVisit.toLocaleString()}</span>
                            </TotalVisits>
                        </>
                    )}
                </HorizontalBarChartHeader>

                {isHorizontalBarChartModalOpen ? (
                    <HorizontalScrollContainer>
                        <ChartWrapper barCount={visibleData.length} isModal={isHorizontalBarChartModalOpen}>
                            <Bar data={chartData} options={options} plugins={[crownPlugin]} />
                        </ChartWrapper>
                    </HorizontalScrollContainer>
                ) : (
                    <ChartWrapper barCount={visibleData.length} isModal={isHorizontalBarChartModalOpen}>
                        <Bar
                            ref={chartRef}
                            data={chartData}
                            options={options}
                            plugins={[crownPlugin, rightLabelPlugin]}
                        />
                    </ChartWrapper>
                )}

                {isHorizontalBarChartModalOpen && (
                    <>
                        {type !== "devices" && (
                            <>
                                <LegendContainer>
                                    <LegendHeader>
                                        <HeaderText1>URL</HeaderText1>
                                        <HeaderText1>Visits</HeaderText1>
                                    </LegendHeader>
                                    {data.map((item, index) => (
                                        <ListItem1 key={item.name} isOdd={index % 2 === 0}>
                                            <Name1 rank={index}>{`${index + 1}. ${item.name}`}</Name1>
                                            <Visits rank={index}>{item.visits.toLocaleString()}</Visits>
                                        </ListItem1>
                                    ))}
                                </LegendContainer>
                            </>
                        )}
                        {type === "devices" && (
                            <>
                                <ModalLegendContainer>
                                    <ModalLegendHeader
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "60% 20% 20%",
                                            alignItems: "center",
                                        }}
                                    >
                                        <HeaderCell>Vendor</HeaderCell>
                                        <HeaderCell style={{ textAlign: "center" }}>Devices</HeaderCell>
                                        <HeaderCell style={{ textAlign: "center" }}>Visits</HeaderCell>
                                    </ModalLegendHeader>

                                    {data.map((item, index) => (
                                        <ListItem
                                            key={item.name}
                                            isOdd={index % 2 === 0}
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "60% 20% 20%",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            {/* Combine ColorSquare + Name into first column */}
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <ColorSquare color={index < 6 ? BarColorPalette[index] : "#57FCFC"} />
                                                <Name>{`${index + 1}. ${item.vendor}`}</Name>
                                            </div>

                                            <VisitNumber style={{ textAlign: "center" }}>{item.model}</VisitNumber>
                                            <Third style={{ textAlign: "center" }}>{item.visits}</Third>
                                        </ListItem>
                                    ))}
                                </ModalLegendContainer>
                            </>
                        )}
                    </>
                )}
                {!isHorizontalBarChartModalOpen && (
                    <ChartFooter>
                        <TotalVisits>
                            Total Visits: <span>{totalVisit.toLocaleString()}</span>
                        </TotalVisits>
                        <ChartDetailsContainer onClick={() => setIsHorizontalBarChartModalOpen(true)}>
                            <ChartDetailText>View more details</ChartDetailText>
                            <ImageContainer
                                src={Chevron}
                                width={"17px"}
                                height={"17px"}
                                padding={"1.324px 0.294px"}
                                alt="chevron"
                                style={{ rotate: "180deg" }}
                            />
                        </ChartDetailsContainer>
                    </ChartFooter>
                )}
            </HorizontalBarChartContainer>
        </>
    );
};

export default HorizontalBarChart;

const HorizontalBarChartContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    @media(max-width: 1440px) {}
`;

const HorizontalBarChartHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 10px;
    }
`;
const ColorSquare = styled.div`
    width: 16px;
    height: 16px;
    background-color: ${({ color }) => color};
    border-radius: 10px;
`;

const HorizontalBarChartTitle = styled.div`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: capitalize;
`;

const FilterText = styled.p`
    color: var(--White---Black-White, #fbfcff);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
        flex: 1;
    }
`;

const ChartWrapper = styled.div`
    position: relative;
    width: 100%;
    height: ${({ barCount, isModal }) => (isModal ? barCount * 35 : 250)}px;
    flex-grow: 1;
    margin-top: 5px;
    canvas {
        width: 100% !important;
        height: 100% !important;
    }

    @media (max-width: 768px) {
        height: ${({ barCount, isModal }) => (isModal ? barCount * 25 : 220)}px;
    }
`;

const TotalVisits = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    text-align: right;
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    span {
        color: var(--Text-Primary, #fbfcff);
        font-family: "Barlow";
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
    }
`;

const ChartFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    margin-top: 25px;
`;

const ChartDetailsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
`;
const ChartDetailText = styled.div`
    color: var(--Purple-Secondary, #9d75f5);
    text-align: right;
    font-family: "Barlow";
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const ModalLegendContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 16px;
    font-family: "Barlow";
`;

const ModalLegendHeader = styled.div`
    display: grid;
    grid-template-columns: 48px minmax(146px, 249px) 324px 153px;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-weight: 600;
    font-size: 16px;
`;

const HeaderCell = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    font-size: 16px;
`;

const ListItem = styled.div`
    display: grid;
    grid-template-columns: 32px minmax(120px, 250px) 60px 60px; /* same as header */
    align-items: center;
    padding: 8px 16px;
    background-color: ${({ isOdd }) => (isOdd ? "rgba(255, 255, 255, 0.04)" : "transparent")};
    column-gap: 8px; /* smaller gap */
`;

const HeaderText = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const Name = styled.div`
    color: var(--Text-Primary, #fbfcff);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const VisitNumber = styled.div`
    color: var(--Text-Primary, #fbfcff);
    text-align: right;
    font-weight: 400;
`;

const Third = styled.div`
    color: var(--Text-Primary, #fbfcff);
    text-align: right;
    font-weight: 600;
`;
const LegendContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    margin-top: 16px;
    max-height: 300px; /* limit height for vertical scrollbar */
    overflow-y: auto; /* vertical scroll when needed */
    overflow-x: hidden;

    /* Custom scrollbar styling for Webkit browsers */
    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: rgba(157, 117, 245, 0.5);
        border-radius: 4px;
    }

    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: rgba(157, 117, 245, 0.5) transparent;
`;

const LegendHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    min-width: 600px; /* or adjust based on your content */
    white-space: nowrap; /* prevent wrapping */
`;

const HeaderText1 = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const ListItem1 = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ isOdd }) => (isOdd ? "10px 15px" : "8px 15px")};
    background-color: ${({ isOdd }) => (isOdd ? "rgba(255, 255, 255, 0.04)" : "transparent")};
    width: 100%;
    box-sizing: border-box;
`;

const Name1 = styled.div`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    min-width: 0;
    text-align: left;
`;

const Visits = styled.div`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-align: left;
`;

const PopupDateTitle = styled.p`
    color: var(--White---Black-Light-Grey, #9d9d9d);
    leading-trim: both;
    text-edge: cap;
    font-family: "Barlow";
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 1.8px;
    text-transform: uppercase;
`;

const HorizontalScrollContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    & > div {
        display: flex;
        min-width: ${({ barCount }) => barCount * 60}px; /* adjust per bar */
    }
`;

const IconWrapper = styled.div`
    position: relative;
    display: inline-block;
    cursor: pointer;
    margin-left: auto;

    &:hover span {
        visibility: visible;
        opacity: 1;
    }
`;

const StyledExportIcon = styled(ExportIcon)`
    width: 40px;
    height: 40px;
`;

const TooltipIcon = styled.span`
    visibility: hidden;
    opacity: 0;
    position: absolute;
    bottom: -30px; /* below the icon */
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    font-size: 12px;
    font-family: "Barlow", sans-serif;
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    transition: opacity 0.2s ease;
`;
